import {
  Modal as MantineModal,
  ModalProps as MantineModalProps,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { ReactNode, useEffect, useState } from 'react';

interface ModalProps extends Omit<MantineModalProps, 'opened'> {
  children: ReactNode;
  opened: boolean;
  onClose: () => void;
}

export default function Modal({
  children,
  opened,
  onClose,
  ...props
}: ModalProps) {
  const isMobile = useMediaQuery('(max-width: 50em)');

  // Resource optimization states
  const [mounted, setMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect -- drives mount/visible animation sequence from the opened prop */
  useEffect(() => {
    if (opened) {
      setMounted(true);

      const openTimer = setTimeout(() => {
        setIsVisible(true);
      }, 10);

      return () => clearTimeout(openTimer);
    } else {
      setIsVisible(false);

      const closeTimer = setTimeout(() => {
        setMounted(false);
      }, 300);

      return () => clearTimeout(closeTimer);
    }
  }, [opened]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleClose = () => {
    onClose();
  };

  // Don't render anything if not mounted
  if (!mounted) return null;

  return (
    <MantineModal
      fullScreen={isMobile}
      size="50dvw"
      shadow="md"
      opened={isVisible}
      onClose={handleClose}
      transitionProps={{ duration: 300 }}
      {...props}
      styles={{
        title: {
          fontWeight: 500,
        },
        header: {
          borderBottom: '1px solid var(--mantine-color-gray-2)',
        },
        body: {
          padding: 0,
        },
        ...props.styles,
      }}
    >
      {children}
    </MantineModal>
  );
}
